'use client';

import { queryOptions, type QueryClient, useQuery } from '@tanstack/react-query';

import { getQueryClient } from 'app/get-query-client';
import type { ServerError } from 'models';
import type { GetLocationResponse } from 'models/reqres';
import { getLocation as getLocationTransport } from 'transport/domains/signup';
import { noop } from 'utils/noop';

// Not user-scoped and never reset on logout — this mirrors geo-location detection,
// which is meaningful for logged-out sessions too (see useReferralCountryEligibility).
export const locationKeys = {
    all: ['location'] as const,
};

function fetchLocation(): Promise<GetLocationResponse> {
    return new Promise((resolve, reject) => {
        getLocationTransport(resolve, reject);
    });
}

function locationQueryOptions() {
    return queryOptions({
        queryKey: locationKeys.all,
        queryFn: fetchLocation,
        staleTime: Infinity,
    });
}

export function useLocationQuery() {
    return useQuery(locationQueryOptions());
}

export function useCountryCode(): string {
    return useLocationQuery().data?.country_iso ?? '';
}

/** Imperative read for non-React modules (lib/metrics, addReferralDialogStore, registrationStore's fallback). */
export function getCountryCodeSnapshot(): string {
    return getQueryClient().getQueryData<GetLocationResponse>(locationKeys.all)?.country_iso ?? '';
}

/**
 * Seeds the cache from a synchronously-known value (e.g. the CDN geo header) without
 * a network round trip. Guarded to the empty case so a real fetched value already in
 * the cache is never clobbered — mirrors UserStore.setCountryCode.
 */
export function seedCountryCode(countryCode: string, queryClient: QueryClient = getQueryClient()): void {
    const existing = queryClient.getQueryData<GetLocationResponse>(locationKeys.all);
    if (existing?.country_iso) return;
    queryClient.setQueryData<GetLocationResponse>(locationKeys.all, {
        country_iso: countryCode,
        state_iso: existing?.state_iso ?? '',
        city_name: existing?.city_name ?? '',
        postal_code: existing?.postal_code ?? '',
    });
}

/**
 * Imperative fetch-and-cache for callback-style consumers that need the full response
 * (or custom onSuc/onErr semantics) rather than the reactive hook. Populates the same
 * cache the hook reads from, so every consumer converges on one resolved value instead
 * of each firing its own independent request — the original getLocation() class method
 * had no such dedup, since it was a plain per-call transport wrapper.
 */
export function loadLocation(
    onSuc: (location: GetLocationResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): void {
    getQueryClient().fetchQuery(locationQueryOptions()).then(onSuc).catch(onErr).finally(onFinally);
}
